// src/pages/MiSuscripcion.jsx
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';

// ─── helpers ─────────────────────────────────────────────────────
const fmt = (n, moneda = 'DOP') =>
  `${moneda} ${parseFloat(n || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}`;

const fmtFecha = (f) =>
  f ? new Date(f + 'T00:00:00').toLocaleDateString('es-DO', { day: '2-digit', month: 'long', year: 'numeric' }) : '—';

const BRAND_ICONS = { visa: '💳', mastercard: '💳', amex: '💳', discover: '💳' };
const brandLabel = (brand) =>
  brand ? brand.charAt(0).toUpperCase() + brand.slice(1) : 'Tarjeta';

const estadoColor = {
  pagada:    { bg: '#dcfce7', color: '#166534', label: 'Pagada' },
  pendiente: { bg: '#fef9c3', color: '#854d0e', label: 'Pendiente' },
  vencida:   { bg: '#fee2e2', color: '#991b1b', label: 'Vencida' },
  fallida:   { bg: '#fee2e2', color: '#991b1b', label: 'Fallida' },
  anulada:   { bg: '#f1f5f9', color: '#475569', label: 'Anulada' },
};

const susEstadoColor = {
  activa:     { bg: '#dcfce7', color: '#166534', label: 'Activa' },
  gracia:     { bg: '#fef9c3', color: '#854d0e', label: 'En período de gracia' },
  suspendida: { bg: '#fee2e2', color: '#991b1b', label: 'Suspendida' },
  cancelada:  { bg: '#f1f5f9', color: '#475569', label: 'Cancelada' },
};

// ─── componente ──────────────────────────────────────────────────
const MiSuscripcion = () => {
  const [sus, setSus]             = useState(null);
  const [facturas, setFacturas]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [loadingFact, setLoadingFact] = useState(true);
  const [error, setError]         = useState(null);

  // Modal agregar/actualizar tarjeta
  const [modalTarjeta, setModalTarjeta] = useState(false);
  const [guardandoTarjeta, setGuardandoTarjeta] = useState(false);
  const [stripeDisponible, setStripeDisponible] = useState(false);
  const [msgTarjeta, setMsgTarjeta] = useState(null);

  // Stripe Elements (lazy — solo cuando se abre el modal)
  const [stripeElements, setStripeElements] = useState(null);
  const [cardElement, setCardElement]       = useState(null);
  const [clientSecret, setClientSecret]     = useState(null);

  useEffect(() => {
    cargarSuscripcion();
    cargarFacturas();
  }, []);

  const cargarSuscripcion = async () => {
    try {
      setLoading(true);
      const r = await api.get('/billing/mi-suscripcion');
      setSus(r.data.data);
    } catch (e) {
      setError('No se pudo cargar la información de tu suscripción.');
    } finally {
      setLoading(false);
    }
  };

  const cargarFacturas = async () => {
    try {
      setLoadingFact(true);
      const r = await api.get('/billing/mis-facturas?limit=24');
      setFacturas(r.data.data || []);
    } catch (_) {
      setFacturas([]);
    } finally {
      setLoadingFact(false);
    }
  };

  // ── Abrir modal de tarjeta ─────────────────────────────────────
  const abrirModalTarjeta = async () => {
    setModalTarjeta(true);
    setMsgTarjeta(null);
    setGuardandoTarjeta(false);

    try {
      // 1. Pedir SetupIntent al backend
      const r = await api.post('/billing/setup-intent');
      const { client_secret } = r.data;
      setClientSecret(client_secret);

      // 2. Cargar Stripe.js dinámicamente
      const pubKey = window.__STRIPE_PK__ || process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
      if (!pubKey || pubKey === 'pk_test_placeholder') {
        setStripeDisponible(false);
        return;
      }

      // Cargar stripe-js si no está cargado
      if (!window.Stripe) {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://js.stripe.com/v3/';
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }

      const stripe = window.Stripe(pubKey);
      const elements = stripe.elements();
      setStripeElements({ stripe, elements });
      setStripeDisponible(true);

      // El CardElement se monta después de que el modal renderice
      setTimeout(() => {
        const card = elements.create('card', {
          hidePostalCode: true,
          style: {
            base: {
              color: '#1e293b',
              fontFamily: "'Segoe UI', sans-serif",
              fontSize: '16px',
              '::placeholder': { color: '#94a3b8' },
            },
            invalid: { color: '#dc2626' },
          },
        });
        card.mount('#stripe-card-element');
        setCardElement(card);
      }, 100);
    } catch (e) {
      setStripeDisponible(false);
      if (e.response?.data?.error?.includes('no está configurado')) {
        setMsgTarjeta({ tipo: 'info', texto: 'El pago con tarjeta estará disponible próximamente. Por ahora, contacta a soporte para procesar el pago manualmente.' });
      } else {
        setMsgTarjeta({ tipo: 'error', texto: e.response?.data?.error || 'Error al conectar con el sistema de pagos.' });
      }
    }
  };

  const cerrarModalTarjeta = () => {
    setModalTarjeta(false);
    setStripeElements(null);
    setCardElement(null);
    setClientSecret(null);
    setMsgTarjeta(null);
  };

  // ── Confirmar tarjeta ──────────────────────────────────────────
  const confirmarTarjeta = async () => {
    if (!stripeElements || !cardElement || !clientSecret) return;
    setGuardandoTarjeta(true);
    setMsgTarjeta(null);

    try {
      const { error: stripeErr, setupIntent } = await stripeElements.stripe.confirmCardSetup(
        clientSecret,
        { payment_method: { card: cardElement } }
      );

      if (stripeErr) {
        setMsgTarjeta({ tipo: 'error', texto: stripeErr.message });
        setGuardandoTarjeta(false);
        return;
      }

      // Enviar pm_id al backend
      await api.post('/billing/guardar-tarjeta', {
        payment_method_id: setupIntent.payment_method,
      });

      setMsgTarjeta({ tipo: 'ok', texto: '✅ Tarjeta guardada. Se usará para futuros cobros automáticos.' });
      setTimeout(() => {
        cerrarModalTarjeta();
        cargarSuscripcion();
      }, 2000);
    } catch (e) {
      setMsgTarjeta({ tipo: 'error', texto: e.response?.data?.error || 'Error al guardar la tarjeta.' });
    } finally {
      setGuardandoTarjeta(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────
  if (loading) return <div style={s.loading}>Cargando suscripción...</div>;
  if (error)   return <div style={s.loading}>{error}</div>;

  return (
    <div style={s.root}>
      <div style={s.container}>

        {/* Header */}
        <div style={s.header}>
          <Link to="/configuracion" style={s.back}>← Volver a Configuración</Link>
          <h1 style={s.title}>Mi Suscripción</h1>
          <p style={s.subtitle}>Gestiona tu plan, método de pago e historial de facturas</p>
        </div>

        {!sus ? (
          <div style={s.card}>
            <p style={{ color: '#64748b', textAlign: 'center', padding: 32 }}>
              No hay una suscripción activa para esta cuenta.<br />
              Contacta a soporte para activar tu plan.
            </p>
          </div>
        ) : (
          <>
            {/* ── Tarjeta estado del plan ── */}
            <div style={s.planCard}>
              <div style={s.planLeft}>
                <span style={s.planIcon}>📦</span>
                <div>
                  <p style={s.planNombre}>{sus.nombre_plan_snapshot}</p>
                  <span style={{
                    ...s.badge,
                    background: susEstadoColor[sus.estado]?.bg || '#f1f5f9',
                    color:      susEstadoColor[sus.estado]?.color || '#475569',
                  }}>
                    {susEstadoColor[sus.estado]?.label || sus.estado}
                  </span>
                </div>
              </div>
              <div style={s.planRight}>
                <div style={s.statBox}>
                  <p style={s.statVal}>{fmt(sus.monto_efectivo, sus.moneda)}</p>
                  <p style={s.statLbl}>por {sus.tipo_ciclo}</p>
                </div>
                <div style={s.statBox}>
                  <p style={{ ...s.statVal, color: sus.dias_restantes <= 5 ? '#dc2626' : '#0f766e' }}>
                    {sus.dias_restantes > 0 ? `${sus.dias_restantes} días` : 'Hoy'}
                  </p>
                  <p style={s.statLbl}>próximo cobro</p>
                </div>
                <div style={s.statBox}>
                  <p style={s.statVal}>{fmtFecha(sus.fecha_proximo_cobro)}</p>
                  <p style={s.statLbl}>fecha cobro</p>
                </div>
              </div>
            </div>

            {/* ── Tarjeta de pago guardada ── */}
            <div style={s.card}>
              <h2 style={s.cardTitle}>💳 Método de Pago</h2>

              {sus.metodo_pago === 'stripe' ? (
                sus.tarjeta && sus.tarjeta.last4 ? (
                  <div style={s.tarjetaRow}>
                    <div style={s.tarjetaInfo}>
                      <span style={s.tarjetaIcon}>{BRAND_ICONS[sus.tarjeta.brand] || '💳'}</span>
                      <div>
                        <p style={s.tarjetaMarca}>{brandLabel(sus.tarjeta.brand)}</p>
                        <p style={s.tarjetaNumero}>•••• •••• •••• {sus.tarjeta.last4}</p>
                        <p style={s.tarjetaExp}>
                          Vence {sus.tarjeta.exp_month?.toString().padStart(2,'0')}/{sus.tarjeta.exp_year}
                        </p>
                      </div>
                    </div>
                    <button onClick={abrirModalTarjeta} style={s.btnSecondary}>
                      Actualizar tarjeta
                    </button>
                  </div>
                ) : (
                  <div style={s.sinTarjeta}>
                    <p style={s.sinTarjetaText}>
                      No hay tarjeta guardada. Agrega una para activar el cobro automático mensual.
                    </p>
                    <button onClick={abrirModalTarjeta} style={s.btnPrimary}>
                      + Agregar tarjeta
                    </button>
                  </div>
                )
              ) : (
                <div style={s.sinTarjeta}>
                  <p style={s.sinTarjetaText}>
                    {sus.metodo_pago === 'transferencia'
                      ? '🏦 Los pagos se procesan por transferencia bancaria.'
                      : sus.metodo_pago === 'azul'
                      ? '🔵 Los pagos se procesan a través de Azul.'
                      : '📋 Los pagos se gestionan manualmente.'}
                    {' '}Contacta a soporte para gestionar tu facturación.
                  </p>
                  <a href="mailto:soporte@efit.com.do"
                    style={{ ...s.btnSecondary, textDecoration: 'none', display: 'inline-block' }}>
                    Contactar soporte
                  </a>
                </div>
              )}
            </div>

            {/* ── Alerta si está en gracia o suspendida ── */}
            {(sus.estado === 'gracia' || sus.estado === 'suspendida') && (
              <div style={{ ...s.alerta, background: sus.estado === 'suspendida' ? '#fee2e2' : '#fef9c3',
                borderColor: sus.estado === 'suspendida' ? '#fca5a5' : '#fde047' }}>
                <p style={{ margin: 0, fontWeight: 600, color: sus.estado === 'suspendida' ? '#991b1b' : '#854d0e' }}>
                  {sus.estado === 'suspendida'
                    ? '⛔ Tu cuenta está suspendida por falta de pago. Agrega una tarjeta o contacta a soporte.'
                    : '⚠️ Tu cuenta tiene facturas pendientes. Tienes un período de gracia activo.'}
                </p>
              </div>
            )}
          </>
        )}

        {/* ── Historial de facturas ── */}
        <div style={s.card}>
          <h2 style={s.cardTitle}>🧾 Historial de Facturas</h2>

          {loadingFact ? (
            <p style={{ color: '#94a3b8', textAlign: 'center', padding: 24 }}>Cargando...</p>
          ) : facturas.length === 0 ? (
            <p style={{ color: '#94a3b8', textAlign: 'center', padding: 24 }}>
              No hay facturas registradas aún.
            </p>
          ) : (
            <div style={s.tabla}>
              <div style={s.tablaHead}>
                <span style={{ flex: 1 }}>Período</span>
                <span style={{ flex: 1.5 }}>Plan</span>
                <span style={{ flex: 1 }}>Monto</span>
                <span style={{ flex: 1 }}>Vencimiento</span>
                <span style={{ flex: 1 }}>Estado</span>
                <span style={{ flex: 1 }}>Pagado</span>
              </div>
              {facturas.map(f => (
                <div key={f.id} style={s.tablaRow}>
                  <span style={{ flex: 1, fontWeight: 600, color: '#1e293b' }}>{f.periodo}</span>
                  <span style={{ flex: 1.5, color: '#475569', fontSize: 13 }}>{f.nombre_plan_snapshot}</span>
                  <span style={{ flex: 1, color: '#0f766e', fontWeight: 600 }}>
                    {fmt(f.monto, f.moneda)}
                    {f.moneda !== 'DOP' && f.monto_dop && (
                      <span style={{ display: 'block', color: '#94a3b8', fontSize: 11, fontWeight: 400 }}>
                        ≈ {fmt(f.monto_dop, 'DOP')}
                      </span>
                    )}
                  </span>
                  <span style={{ flex: 1, color: '#64748b', fontSize: 13 }}>{fmtFecha(f.fecha_vencimiento)}</span>
                  <span style={{ flex: 1 }}>
                    <span style={{
                      ...s.badge,
                      background: estadoColor[f.estado]?.bg || '#f1f5f9',
                      color:      estadoColor[f.estado]?.color || '#475569',
                    }}>
                      {estadoColor[f.estado]?.label || f.estado}
                    </span>
                  </span>
                  <span style={{ flex: 1, color: '#64748b', fontSize: 13 }}>
                    {f.fecha_pago ? fmtFecha(f.fecha_pago.split('T')[0]) : '—'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Info de contacto ── */}
        <div style={s.contacto}>
          <p style={{ margin: 0, color: '#64748b', fontSize: 13 }}>
            ¿Necesitas cambiar de plan o tienes algún problema con tu suscripción?{' '}
            <a href="mailto:soporte@efit.com.do" style={{ color: '#6366f1', fontWeight: 600 }}>
              Contacta a soporte
            </a>
          </p>
        </div>

      </div>

      {/* ════════════════════════════════════════════════════════
          MODAL — Agregar / Actualizar tarjeta
      ════════════════════════════════════════════════════════ */}
      {modalTarjeta && (
        <div style={s.overlay}>
          <div style={s.modal}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitle}>
                {sus?.tarjeta?.last4 ? 'Actualizar tarjeta' : 'Agregar tarjeta de crédito/débito'}
              </h2>
              <button onClick={cerrarModalTarjeta} style={s.btnCerrar}>✕</button>
            </div>

            <div style={s.modalBody}>

              {/* Mensaje de estado */}
              {msgTarjeta && (
                <div style={{
                  padding: '10px 14px', borderRadius: 8, marginBottom: 16,
                  background: msgTarjeta.tipo === 'ok' ? '#dcfce7' : msgTarjeta.tipo === 'info' ? '#eff6ff' : '#fee2e2',
                  color:      msgTarjeta.tipo === 'ok' ? '#166534' : msgTarjeta.tipo === 'info' ? '#1e40af' : '#991b1b',
                  fontSize: 14,
                }}>
                  {msgTarjeta.texto}
                </div>
              )}

              {/* Stripe Elements o placeholder */}
              {stripeDisponible ? (
                <>
                  <p style={{ color: '#64748b', fontSize: 13, marginBottom: 12 }}>
                    Ingresa los datos de tu tarjeta. Tu información está protegida por Stripe y nunca toca nuestros servidores.
                  </p>
                  <div style={s.cardElementWrap}>
                    <div id="stripe-card-element" style={{ padding: '12px 4px' }} />
                  </div>
                  <div style={s.modalFooter}>
                    <button onClick={cerrarModalTarjeta} style={s.btnSecondary} disabled={guardandoTarjeta}>
                      Cancelar
                    </button>
                    <button onClick={confirmarTarjeta} style={s.btnPrimary} disabled={guardandoTarjeta}>
                      {guardandoTarjeta ? 'Procesando...' : '✅ Guardar tarjeta'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* Placeholder visual cuando Stripe no está activo */}
                  {!msgTarjeta && (
                    <p style={{ color: '#64748b', fontSize: 13, marginBottom: 16, lineHeight: 1.6 }}>
                      El pago con tarjeta estará disponible próximamente.<br />
                      Para procesar tu pago ahora, contáctanos directamente.
                    </p>
                  )}

                  <div style={s.placeholderCard}>
                    <div style={s.placeholderRow}>
                      <div style={s.placeholderField}>
                        <label style={s.placeholderLabel}>Número de tarjeta</label>
                        <div style={s.placeholderInput}>•••• •••• •••• ••••</div>
                      </div>
                    </div>
                    <div style={s.placeholderRow}>
                      <div style={{ ...s.placeholderField, flex: 1 }}>
                        <label style={s.placeholderLabel}>Vencimiento</label>
                        <div style={s.placeholderInput}>MM / AA</div>
                      </div>
                      <div style={{ ...s.placeholderField, flex: 1 }}>
                        <label style={s.placeholderLabel}>CVC</label>
                        <div style={s.placeholderInput}>•••</div>
                      </div>
                    </div>
                    <div style={{ ...s.placeholderRow, justifyContent: 'center', marginTop: 8 }}>
                      <span style={{ fontSize: 12, color: '#94a3b8' }}>
                        🔒 Formulario activo cuando se configuren las claves Stripe
                      </span>
                    </div>
                  </div>

                  <div style={s.modalFooter}>
                    <button onClick={cerrarModalTarjeta} style={s.btnSecondary}>Cerrar</button>
                    <a href="mailto:soporte@efit.com.do" style={{ ...s.btnPrimary, textDecoration: 'none' }}>
                      Contactar soporte
                    </a>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// ESTILOS
// ═══════════════════════════════════════════════════════════════════
const s = {
  root:       { minHeight: '100vh', background: '#f8fafc', fontFamily: "'Segoe UI', sans-serif" },
  container:  { maxWidth: 900, margin: '0 auto', padding: '24px 20px' },
  loading:    { padding: 60, textAlign: 'center', color: '#64748b', fontSize: 16 },

  header:     { marginBottom: 28 },
  back:       { color: '#6366f1', textDecoration: 'none', fontSize: 14, fontWeight: 600 },
  title:      { fontSize: 28, fontWeight: 700, color: '#1e293b', margin: '10px 0 4px' },
  subtitle:   { fontSize: 14, color: '#64748b', margin: 0 },

  // Plan card
  planCard: {
    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
    borderRadius: 16, padding: '24px 28px', marginBottom: 20,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    flexWrap: 'wrap', gap: 16, color: '#fff',
    boxShadow: '0 8px 24px rgba(99,102,241,0.3)',
  },
  planLeft:   { display: 'flex', alignItems: 'center', gap: 16 },
  planIcon:   { fontSize: 40 },
  planNombre: { fontSize: 22, fontWeight: 700, margin: '0 0 6px' },
  planRight:  { display: 'flex', gap: 32, flexWrap: 'wrap' },
  statBox:    { textAlign: 'center' },
  statVal:    { fontSize: 20, fontWeight: 700, margin: '0 0 2px', color: '#fff' },
  statLbl:    { fontSize: 11, margin: 0, color: 'rgba(255,255,255,0.75)', textTransform: 'uppercase', letterSpacing: 1 },

  badge: {
    display: 'inline-block', padding: '2px 10px', borderRadius: 20,
    fontSize: 12, fontWeight: 600,
  },

  // Tarjeta de pago
  card: {
    background: '#fff', borderRadius: 12, padding: '24px',
    marginBottom: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    border: '1px solid #e2e8f0',
  },
  cardTitle: { fontSize: 18, fontWeight: 700, color: '#1e293b', margin: '0 0 16px' },

  tarjetaRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    flexWrap: 'wrap', gap: 16,
  },
  tarjetaInfo: { display: 'flex', alignItems: 'center', gap: 16 },
  tarjetaIcon: { fontSize: 36 },
  tarjetaMarca: { fontSize: 16, fontWeight: 700, color: '#1e293b', margin: '0 0 2px' },
  tarjetaNumero: { fontSize: 20, letterSpacing: 3, color: '#334155', margin: '0 0 2px', fontVariantNumeric: 'tabular-nums' },
  tarjetaExp:  { fontSize: 12, color: '#94a3b8', margin: 0 },

  sinTarjeta: { textAlign: 'center', padding: '24px 0' },
  sinTarjetaText: { color: '#64748b', marginBottom: 16, fontSize: 14 },

  // Alerta
  alerta: {
    border: '1px solid', borderRadius: 10, padding: '14px 18px', marginBottom: 20,
  },

  // Tabla facturas
  tabla: { border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' },
  tablaHead: {
    display: 'flex', background: '#f8fafc', padding: '10px 16px',
    borderBottom: '1px solid #e2e8f0', fontSize: 12, fontWeight: 700,
    color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5,
  },
  tablaRow: {
    display: 'flex', padding: '12px 16px', borderBottom: '1px solid #f1f5f9',
    alignItems: 'center', transition: 'background 0.15s',
  },

  contacto: {
    background: '#f8fafc', border: '1px solid #e2e8f0',
    borderRadius: 10, padding: '14px 18px', textAlign: 'center',
  },

  // Botones
  btnPrimary: {
    background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8,
    padding: '10px 20px', fontWeight: 600, fontSize: 14, cursor: 'pointer',
    display: 'inline-block',
  },
  btnSecondary: {
    background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0',
    borderRadius: 8, padding: '10px 20px', fontWeight: 600, fontSize: 14, cursor: 'pointer',
  },

  // Modal
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
  },
  modal: {
    background: '#fff', borderRadius: 16, width: '100%', maxWidth: 480,
    margin: 16, boxShadow: '0 24px 64px rgba(0,0,0,0.18)',
    maxHeight: '90vh', overflowY: 'auto',
  },
  modalHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '20px 24px 0', borderBottom: '1px solid #f1f5f9', paddingBottom: 16,
  },
  modalTitle: { fontSize: 18, fontWeight: 700, color: '#1e293b', margin: 0 },
  btnCerrar: {
    background: 'none', border: 'none', fontSize: 20, cursor: 'pointer',
    color: '#94a3b8', padding: 4,
  },
  modalBody: { padding: '20px 24px' },
  modalFooter: {
    display: 'flex', gap: 10, justifyContent: 'flex-end',
    marginTop: 20, paddingTop: 16, borderTop: '1px solid #f1f5f9',
  },

  // Stripe card element wrapper
  cardElementWrap: {
    border: '1px solid #cbd5e1', borderRadius: 8, padding: '4px 12px',
    background: '#f8fafc', marginBottom: 4,
  },

  // Placeholder tarjeta (cuando Stripe no está activo)
  placeholderCard: {
    border: '2px dashed #e2e8f0', borderRadius: 12, padding: 20,
    background: '#f8fafc', marginBottom: 8,
  },
  placeholderRow: { display: 'flex', gap: 16, marginBottom: 12 },
  placeholderField: { display: 'flex', flexDirection: 'column', flex: 2 },
  placeholderLabel: { fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 },
  placeholderInput: {
    border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 14px',
    color: '#cbd5e1', fontSize: 16, background: '#fff', letterSpacing: 3,
  },
};

export default MiSuscripcion;
